import type { FormEvent, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { TranscriptUpdate } from "@frontierx/shared/schemas/TranscriptUpdate";
import type {
  BaseClientMessage,
  ClientMessage,
} from "@frontierx/shared/schemas/clientMessageTypes";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  XCircle,
} from "lucide-react";
import { CompactConversationSimulator } from "../LiveGuidance/CompactConversationSimulator";

// Import the existing type from the hook
import type { UseCallDisplayReturn } from "../Call/hooks/useCallDisplay";

interface ScriptDetectionLog {
  timestamp: number;
  scriptItemId: string;
  scriptItemText: string;
  triggeringText: string;
  checksPerformed: Array<{
    method: string;
    score: number;
    partialThreshold: number;
    fullThreshold: number;
  }>;
  decision: "completed" | "partial" | "no_match";
  reason: string;
  similarity?: number;
}

export interface CallTestingPanelProps {
  botSocket: UseCallDisplayReturn["botSocket"];
  transcript: TranscriptUpdate[];
  scriptDetectionLogs: ScriptDetectionLog[];
}

export const CallTestingPanel = ({
  botSocket,
  transcript,
  scriptDetectionLogs,
}: Readonly<CallTestingPanelProps>) => {
  const maxTime = useMemo(() => {
    return Math.max(
      ...transcript.flatMap(
        (item) => item.words?.map((word) => word.end_time) ?? [],
      ),
      0, // Fallback value if the array is empty
    );
  }, [transcript]);
  return (
    <div className="flex w-full flex-col space-y-4 divide-y border-gray-400 p-4">
      <CallTestingSection title="Simulate a conversation">
        <div className="flex flex-col space-y-4">
          <SimulateConversation
            title={"Speak as the host"}
            mode={"host"}
            botSocket={botSocket}
            time={maxTime}
          />

          <SimulateConversation
            title={"Speak as the guest"}
            mode={"guest"}
            botSocket={botSocket}
            time={maxTime}
          />
        </div>
      </CallTestingSection>
      <CallTestingSection title="Simulate events">
        <SimulateEventsMenu botSocket={botSocket} />
      </CallTestingSection>
      <CallTestingSection title="Conversation Simulator">
        <CompactConversationSimulator botSocket={botSocket} />
      </CallTestingSection>
      <CallTestingSection title="Script Detection Debug">
        <ScriptDetectionLogsDisplay logs={scriptDetectionLogs} />
      </CallTestingSection>
    </div>
  );
};

export interface SimulateEventsMenuProps {
  botSocket: UseCallDisplayReturn["botSocket"];
}

export const SimulateEventsMenu = ({
  botSocket,
}: Readonly<SimulateEventsMenuProps>) => {
  const sendClientEvent = useCallback(
    (message: BaseClientMessage) => {
      const messageWithTimestamp: ClientMessage = {
        ...message,
        timestamp: new Date().toISOString(),
      };
      console.log("Sending client event:", messageWithTimestamp);
      botSocket.send(JSON.stringify(messageWithTimestamp));
    },
    [botSocket],
  );

  const handleReset = useCallback(() => {
    sendClientEvent({ type: "RESET" });
  }, [sendClientEvent]);

  const handleSimulateBuyingSignal = useCallback(() => {
    sendClientEvent({ type: "SIMULATE_BUYING_SIGNAL" });
  }, [sendClientEvent]);

  const handleSimulateQuestion = useCallback(() => {
    sendClientEvent({ type: "SIMULATE_QUESTION" });
  }, [sendClientEvent]);

  const handleSimulateConversation = useCallback(() => {
    sendClientEvent({ type: "SIMULATE_CONVERSATION" });
  }, [sendClientEvent]);

  const handleSimulateFullScript = useCallback(() => {
    sendClientEvent({ type: "SIMULATE_FULL_SCRIPT" });
  }, [sendClientEvent]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          Select event
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={handleSimulateBuyingSignal}>
          Simulate buying signal
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSimulateQuestion}>
          Simulate question
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSimulateConversation}>
          Simulate conversation
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSimulateFullScript}>
          Simulate full script
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleReset}>Reset</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface SimulateConversationProps {
  title: string;
  mode: string;
  botSocket: UseCallDisplayReturn["botSocket"];
  time: number;
}

export const SimulateConversation = ({
  title,
  mode,
  botSocket,
  time,
}: Readonly<SimulateConversationProps>) => {
  const [inputText, setInputText] = useState("");

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inputText.trim()) {
      botSocket.send(
        JSON.stringify({
          type: "SIMULATE_TRANSCRIPT",
          payload: { mode, text: inputText.trim(), time: time + 1 },
          timestamp: new Date().toISOString(),
        }),
      );
      setInputText("");
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <h3 className="font-bold">{title}</h3>
      <form onSubmit={handleSubmit} className="flex space-x-4">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow rounded border px-2 py-1"
          placeholder="Enter text..."
        />
        <button
          type="submit"
          className="rounded bg-blue-500 px-4 py-1 text-white hover:bg-blue-600"
        >
          Speak
        </button>
      </form>
    </div>
  );
};

export interface CallTestingSectionProps {
  title: string;
  children: ReactNode;
}
export const CallTestingSection = ({
  title,
  children,
}: Readonly<CallTestingSectionProps>) => {
  return (
    <div className="flex w-full flex-col space-y-4 p-4">
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  );
};

const DecisionIcon = ({
  decision,
}: {
  decision: ScriptDetectionLog["decision"];
}) => {
  switch (decision) {
    case "completed":
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case "partial":
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    case "no_match":
      return <XCircle className="h-4 w-4 text-red-600" />;
  }
};

const formatTimestamp = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 1,
  });
};

export function ScriptDetectionLogsDisplay({
  logs,
}: Readonly<{ logs: ScriptDetectionLog[] }>) {
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());

  const toggleLogExpansion = (index: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg text-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-300 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="font-medium">Script Detection Logs</span>
          <span className="text-gray-600">({logs.length} logs)</span>
        </div>
      </div>

      {/* Logs Content */}
      <div className="max-h-96 overflow-y-auto">
        {logs.length === 0 ? (
          <div className="p-4 text-gray-500 text-center">
            No script detection logs yet. Start speaking to see analysis...
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs
              .slice()
              .reverse()
              .map((log, reverseIndex) => {
                const index = logs.length - 1 - reverseIndex; // Original index
                const isLogExpanded = expandedLogs.has(index);

                return (
                  <div key={`${log.timestamp}-${index}`} className="p-3">
                    {/* Log Header */}
                    <button
                      className="flex items-start gap-2 w-full text-left hover:bg-gray-50 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => toggleLogExpansion(index)}
                      type="button"
                    >
                      <DecisionIcon decision={log.decision} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gray-500 font-mono text-xs">
                            {formatTimestamp(log.timestamp)}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              log.decision === "completed"
                                ? "bg-green-100 text-green-800"
                                : log.decision === "partial"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                            }`}
                          >
                            {log.decision.toUpperCase()}
                          </span>
                          {log.similarity !== undefined && (
                            <span className="text-blue-600 font-mono text-xs">
                              {log.similarity.toFixed(3)}
                            </span>
                          )}
                        </div>
                        <div
                          className="text-gray-900 truncate font-medium"
                          title={log.scriptItemText}
                        >
                          "{log.scriptItemText}"
                        </div>
                        <div
                          className="text-gray-600 text-xs mt-1 truncate"
                          title={log.reason}
                        >
                          {log.reason}
                        </div>
                      </div>
                      {isLogExpanded ? (
                        <ChevronUp className="h-3 w-3 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-3 w-3 text-gray-400" />
                      )}
                    </button>

                    {/* Expanded Details */}
                    {isLogExpanded && (
                      <div className="mt-3 ml-6 space-y-3 text-xs">
                        {/* Triggering Text */}
                        <div>
                          <div className="text-gray-700 font-medium mb-1">
                            Triggering Text:
                          </div>
                          <div className="bg-gray-100 p-2 rounded text-gray-900 break-words border">
                            "{log.triggeringText}"
                          </div>
                        </div>

                        {/* Checks Performed */}
                        <div>
                          <div className="text-gray-700 font-medium mb-1">
                            Checks Performed:
                          </div>
                          <div className="space-y-1">
                            {log.checksPerformed.map((check, checkIndex) => (
                              <div
                                key={checkIndex}
                                className="bg-gray-100 p-2 rounded border"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-blue-700 font-medium">
                                    {check.method}
                                  </span>
                                  <span
                                    className={`font-bold ${
                                      check.score >= check.fullThreshold
                                        ? "text-green-600"
                                        : check.score >= check.partialThreshold
                                          ? "text-yellow-600"
                                          : "text-red-600"
                                    }`}
                                  >
                                    {check.score.toFixed(3)}
                                  </span>
                                </div>
                                <div className="text-gray-600 text-xs">
                                  Partial: {check.partialThreshold.toFixed(3)} |
                                  Full: {check.fullThreshold.toFixed(3)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Decision Reasoning */}
                        <div>
                          <div className="text-gray-700 font-medium mb-1">
                            Decision Reasoning:
                          </div>
                          <div className="bg-gray-100 p-2 rounded text-gray-900 border">
                            {log.reason}
                          </div>
                        </div>

                        {/* Script Item ID */}
                        <div className="text-gray-500 text-xs font-mono">
                          Script Item ID: {log.scriptItemId}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
